<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { worldStore, dispatch, exportSave, importSave, hardReset } from '@sim/store';
  import { floorCost, activeBuilding } from '@sim/world';
  import { SECURITY_UPGRADES, securityCost } from '@sim/reducer';
  import { money, moodBand, roleLabel, activityLabel, defenseSummary } from '@sim/selectors';

  export let tab: string;
  /** docked = live in a persistent side rail (desktop) instead of a bottom sheet */
  export let docked = false;
  const emit = createEventDispatcher();
  $: city = $worldStore;
  $: b = activeBuilding(city)!;
  $: def = defenseSummary(b, city.t);

  let exported = '';
  let importText = '';
  let status = '';
  let confirming = false;

  function doExport() {
    exported = exportSave();
    status = 'Save copied below.';
    if (navigator.clipboard) navigator.clipboard.writeText(exported).catch(() => {});
  }
  function doImport() {
    status = importSave(importText.trim()) ? '✅ Loaded.' : '❌ Bad save code.';
  }
  function doReset() {
    if (!confirming) {
      confirming = true;
      return;
    }
    hardReset();
    status = 'Wiped.';
    confirming = false;
  }

  const tabs = [
    ['crew', '👥 Crew'],
    ['build', '🏗️ Build'],
    ['security', '🛡️ Security'],
    ['log', '📜 Log'],
    ['settings', '⚙️'],
  ];
</script>

{#snippet inner()}
    <div class="tabs">
      {#each tabs as [id, label]}
        <button class="t" class:on={tab === id} on:click={() => emit('tab', id)}>{label}</button>
      {/each}
      {#if !docked}<button class="x" on:click={() => emit('close')}>✕</button>{/if}
    </div>

    <div class="body">
      {#if tab === 'crew'}
        <div class="hint">{b.name} · {b.colonists.length}/{b.maxCrew} crew</div>
        {#each b.colonists as c (c.id)}
          <button class="crow" on:click={() => emit('selectColonist', c.id)}>
            <span class="dot" style="background:{moodBand(c.mood).color}"></span>
            <span class="cn">{c.name}</span>
            <span class="cr">{roleLabel(c)}</span>
            <span class="ca">{activityLabel(c)}</span>
          </button>
        {/each}
      {:else if tab === 'build'}
        <p class="hint">Tap an empty slot in the tower to build a room. Grow rooms in the basement draw less HEAT. Expand {b.name}:</p>
        <button class="big" disabled={city.cash < floorCost(b)} on:click={() => dispatch({ type: 'addFloor' })}>
          ⬆️ Build Up (floor {b.floorMax + 1}) <span class="mono">{money(floorCost(b))}</span>
        </button>
        <button class="big" disabled={city.cash < floorCost(b)} on:click={() => dispatch({ type: 'digBasement' })}>
          ⬇️ Dig Basement (B{-b.floorMin + 1}) <span class="mono">{money(floorCost(b))}</span>
        </button>
      {:else if tab === 'security'}
        <div class="def mono">{b.name}: Defense {def.defense} · Safe HEAT ≤ {def.safe}</div>
        {#each Object.entries(SECURITY_UPGRADES) as [id, u]}
          {@const lvl = b.security[id] ?? 0}
          {@const cost = securityCost(b, id)}
          <div class="srow">
            <div class="si">
              <span class="sn">{u.name} <span class="lv mono">{lvl}/{u.max}</span></span>
              <span class="sb">{u.blurb}</span>
            </div>
            {#if lvl >= u.max}
              <span class="max">MAX</span>
            {:else}
              <button class="buy" disabled={city.cash < cost} on:click={() => dispatch({ type: 'buySecurity', id })}>{money(cost)}</button>
            {/if}
          </div>
        {/each}
      {:else if tab === 'log'}
        <div class="log">
          {#each [...city.log].reverse() as e}
            <div class="le {e.kind}"><span class="lt mono">D{e.day}</span> {e.text}</div>
          {/each}
        </div>
      {:else}
        <button class="big" on:click={doExport}>Export save (copies to clipboard)</button>
        {#if exported}<textarea class="mono" rows="2" readonly>{exported}</textarea>{/if}
        <textarea class="mono" rows="2" bind:value={importText} placeholder="paste save code…"></textarea>
        <button class="big" disabled={!importText.trim()} on:click={doImport}>Load save</button>
        <button class="big danger" class:armed={confirming} on:click={doReset}>{confirming ? 'Tap again to wipe' : 'Hard reset'}</button>
        {#if status}<div class="status">{status}</div>{/if}
      {/if}
    </div>
  {/snippet}

  {#if docked}
    <div class="sheet docked">{@render inner()}</div>
  {:else}
    <div class="scrim" on:click|self={() => emit('close')} role="presentation">
      <div class="sheet">
        <div class="grab"></div>
        {@render inner()}
      </div>
    </div>
  {/if}

<style>
  .scrim {
    position: absolute;
    inset: 0;
    z-index: 55;
    display: flex;
    align-items: flex-end;
    background: rgba(6, 6, 12, 0.6);
  }
  .sheet {
    width: 100%;
    max-height: 76%;
    display: flex;
    flex-direction: column;
    background: var(--panel);
    border-top-left-radius: 18px;
    border-top-right-radius: 18px;
    border-top: 1px solid var(--line);
    animation: rise 0.22s ease both;
  }
  /* docked into a desktop side rail: fill height, no sheet chrome */
  .sheet.docked {
    width: 100%;
    max-height: none;
    height: 100%;
    flex: 1;
    min-height: 0;
    border-radius: 0;
    border-top: none;
    background: var(--bg-2);
    animation: none;
  }
  .sheet.docked .body {
    flex: 1;
    min-height: 0;
    padding-bottom: 14px;
  }
  .grab {
    width: 38px;
    height: 4px;
    border-radius: 4px;
    background: var(--line);
    margin: 8px auto 6px;
  }
  .tabs {
    display: flex;
    gap: 4px;
    padding: 0 10px 8px;
    border-bottom: 1px solid var(--line);
    align-items: center;
  }
  .t {
    flex: 1;
    padding: 7px 4px;
    border-radius: 8px;
    font-size: 11px;
    color: var(--ink-dim);
    background: none;
    border: none;
  }
  .t.on {
    background: var(--panel-2);
    color: var(--ink);
    box-shadow: inset 0 -2px 0 var(--good);
  }
  .x {
    font-size: 15px;
    color: var(--ink-dim);
    padding: 4px 8px;
  }
  .body {
    overflow-y: auto;
    padding: 12px 14px calc(16px + env(safe-area-inset-bottom));
    display: flex;
    flex-direction: column;
    gap: 7px;
  }
  .crow {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px;
    border-radius: 9px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    color: var(--ink);
    font-size: 12px;
  }
  .dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
  }
  .cn {
    font-weight: 700;
  }
  .cr {
    color: var(--ink-dim);
  }
  .ca {
    margin-left: auto;
    color: var(--ink-faint);
    font-size: 11px;
  }
  .hint {
    font-size: 12px;
    color: var(--ink-dim);
    margin: 0 0 4px;
  }
  .big {
    padding: 11px;
    border-radius: 9px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    color: var(--ink);
    font-weight: 700;
    font-size: 13px;
  }
  .big.danger {
    border-color: var(--bad);
    color: var(--bad);
  }
  .big.armed {
    background: color-mix(in srgb, var(--bad) 16%, var(--panel-2));
  }
  textarea {
    width: 100%;
    background: #000;
    color: var(--ink);
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 7px;
    font-size: 11px;
    resize: vertical;
  }
  .def {
    font-size: 12px;
    color: var(--heat-cool);
    margin-bottom: 4px;
  }
  .srow {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px;
    border-radius: 9px;
    background: var(--panel-2);
    border: 1px solid var(--line);
  }
  .si {
    flex: 1;
  }
  .sn {
    font-weight: 700;
    font-size: 12px;
  }
  .lv {
    color: var(--ink-faint);
    font-weight: 500;
    font-size: 11px;
  }
  .sb {
    display: block;
    font-size: 10px;
    color: var(--ink-dim);
  }
  .buy {
    padding: 7px 10px;
    border-radius: 8px;
    border: 1px solid var(--cash);
    color: var(--cash);
    background: none;
    font-size: 12px;
    font-weight: 700;
  }
  .max {
    color: var(--good);
    font-weight: 800;
    font-size: 11px;
  }
  .log {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .le {
    font-size: 11px;
    color: var(--ink-dim);
    padding: 5px 7px;
    border-radius: 6px;
    background: var(--panel-2);
  }
  .le .lt {
    color: var(--ink-faint);
    margin-right: 5px;
  }
  .le.good {
    color: var(--good);
  }
  .le.bad {
    color: var(--bad);
  }
  .le.raid {
    color: var(--heat-crit);
  }
  .status {
    font-size: 12px;
    color: var(--good);
  }
</style>
